import React, { useState } from "react";
import BustaPagaSimulata from "./BustaPagaSimulata";

// Risultato di una simulazione calcolata a regole (metodo_calcolo = 'regole').
// Legge tutto da dettaglio_calcolo, cioe' l'output di calcolaSimulazione() salvato dal backend.

const formatEur = (value) => {
    if (value == null) return "-";
    return `${Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} €`;
};
const formatOre = (value) =>
    value == null ? "-" : Number(value).toLocaleString("it-IT", { maximumFractionDigits: 2, useGrouping: "always" });
const formatPerc = (value) =>
    value == null ? "-" : `${(Number(value) * 100).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;

// Righe delle addizionali da mostrare (risultato e busta), con etichetta, base di calcolo e importo.
// - mese preciso: rate dell'anno precedente come nelle buste (regionale, comunale saldo, acconto comunale)
// - mese tipo: regionale e comunale medie (annue / 12)
// - simulazioni salvate prima, senza regione: null -> si mostra la riga unica con l'importo fisso
const eur2 = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });
export const righeAddizionali = (d) => {
    const info = d.addizionali;
    if (!info || !info.regione) return null;
    const percComunale = `${(info.aliquotaComunale * 100).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`;
    const righe = info.rateMese
        ? [
            { label: `Rata addizionale regionale anno prec. (${info.regione})`, base: `${eur2(info.regionaleAnnua)} ÷ 11 rate`, valore: info.rateMese.regionale },
            { label: "Rata addizionale comunale anno prec.", base: `${eur2(info.comunaleAnnua)} × 70% ÷ 11 rate`, valore: info.rateMese.comunaleSaldo },
            { label: "Acconto addizionale comunale", base: `${eur2(info.comunaleAnnua)} × 30% ÷ 9 rate`, valore: info.rateMese.comunaleAcconto }
        ].filter(x => x.valore > 0)
        : [
            { label: `Addizionale regionale (${info.regione})`, base: `${eur2(info.regionaleAnnua)} annui ÷ 12`, valore: Math.round(info.regionaleAnnua / 12 * 100) / 100 },
            { label: `Addizionale comunale (${percComunale})`, base: `${eur2(d.annuo.redditoImponibile)} × ${percComunale} ÷ 12`, valore: 0 }
        ];
    // l'ultima riga prende il resto, cosi' la somma e' esattamente il totale del mese
    if (righe.length) {
        const altre = righe.slice(0, -1).reduce((t, x) => t + x.valore, 0);
        righe[righe.length - 1].valore = Math.round((d.mensile.addizionali - altre) * 100) / 100;
    }
    return righe;
};

// Riga di una tabella di dettaglio. tipo: "meno" / "piu" (segno davanti), "totale" (riga in grassetto)
const Riga = ({ label, valore, tipo }) => (
    <tr className={tipo === "totale" ? "dettaglio-totale" : ""}>
        <td>{tipo === "meno" ? "− " : tipo === "piu" ? "+ " : ""}{label}</td>
        <td className="dettaglio-valore">{valore}</td>
    </tr>
);

const RisultatoRegole = ({ risultato }) => {
    const [mostraBusta, setMostraBusta] = useState(false);
    const d = risultato.dettaglio_calcolo;
    if (!d) return null;
    const { mensile, costoAzienda, monteOre, annuo, retribuzione } = d;
    const a = monteOre.assenze;
    const add = righeAddizionali(d);
    const mese = d.busta && d.busta.nomeMese;   // null = mese tipo (o simulazione salvata prima)
    const aggiuntiveInBusta = (d.busta && d.busta.aggiuntive) || [];

    return (
        <div className="ai-risultato simulatore-risultato">
            <h4 className="ai-risultato-title">
                Risultato simulazione
                <span className="badge-metodo badge-regole">Regole fiscali {d.regole}</span>
                {risultato.created_by_username && (
                    <span className="risultato-creato-da">
                        {" — creato da "}{risultato.created_by_username}
                    </span>
                )}
                <button
                    type="button"
                    className="btn-mini btn-mini-primary btn-busta-paga"
                    onClick={() => setMostraBusta(true)}
                >
                    Busta paga simulata
                </button>
            </h4>

            {mostraBusta && (
                <BustaPagaSimulata risultato={risultato} onClose={() => setMostraBusta(false)} />
            )}

            <div className="kpi-grid">
                <div className="kpi-card">
                    <span className="kpi-label">{mese ? `Netto di ${mese}` : "Netto mensile"}</span>
                    <span className="kpi-value">{formatEur(mensile.netto)}</span>
                    <span className="kpi-sub">lordo {formatEur(mensile.lordo)}</span>
                </div>
                <div className="kpi-card">
                    <span className="kpi-label">Costo azienda annuo</span>
                    <span className="kpi-value">{formatEur(costoAzienda.totaleAnnuo)}</span>
                    <span className="kpi-sub">{formatEur(costoAzienda.totaleMensile)} al mese</span>
                </div>
                <div className="kpi-card kpi-evidenza">
                    <span className="kpi-label">Costo orario reale</span>
                    <span className="kpi-value">{formatEur(costoAzienda.orario)}</span>
                    <span className="kpi-sub">su {formatOre(monteOre.oreLavorate)} ore lavorate</span>
                </div>
                <div className="kpi-card">
                    <span className="kpi-label">RAL (lordo annuo)</span>
                    <span className="kpi-value">{formatEur(annuo.lordo)}</span>
                    <span className="kpi-sub">netto annuo {formatEur(annuo.netto)}</span>
                </div>
            </div>

            <div className="dettaglio-grid">
                <div className="dettaglio-box">
                    <h5 className="dettaglio-title">Dal lordo al netto ({mese ? `busta di ${mese}` : "mese tipo"})</h5>
                    <table className="dettaglio-table">
                        <tbody>
                            <Riga label={`Retribuzione lorda (part-time ${formatPerc(retribuzione.percPartTime)})`} valore={formatEur(mensile.lordo)} />
                            <Riga label="Contributi a carico lavoratore" valore={formatEur(mensile.ivs)} tipo="meno" />
                            <Riga label="IRPEF lorda" valore={formatEur(mensile.irpefLorda)} tipo="meno" />
                            <Riga label="Detrazioni lavoro dipendente" valore={formatEur(mensile.detrazioneLavoro)} tipo="piu" />
                            {mensile.ulterioreDetrazione > 0 && (
                                <Riga label="Ulteriore detrazione (cuneo)" valore={formatEur(mensile.ulterioreDetrazione)} tipo="piu" />
                            )}
                            {mensile.conguaglioUlterioreDetrazione > 0 && (
                                <Riga label="Ulteriore detrazione cuneo (conguaglio di dicembre)" valore={formatEur(mensile.conguaglioUlterioreDetrazione)} tipo="piu" />
                            )}
                            {mensile.detrazioniFamiliari > 0 && (
                                <Riga label="Detrazioni familiari" valore={formatEur(mensile.detrazioniFamiliari)} tipo="piu" />
                            )}
                            {mensile.impostaSostitutiva > 0 && (
                                <Riga label="Imposta 5% su aumenti rinnovo CCNL" valore={formatEur(mensile.impostaSostitutiva)} tipo="meno" />
                            )}
                            {add ? (
                                add.map(x => <Riga key={x.label} label={x.label} valore={formatEur(x.valore)} tipo="meno" />)
                            ) : (
                                <Riga label="Addizionali regionali/comunali" valore={formatEur(mensile.addizionali)} tipo="meno" />
                            )}
                            {mensile.sommaEsente > 0 && (
                                <Riga label="Somma esente (taglio cuneo)" valore={formatEur(mensile.sommaEsente)} tipo="piu" />
                            )}
                            {mensile.trattamentoIntegrativo > 0 && (
                                <Riga label="Trattamento integrativo" valore={formatEur(mensile.trattamentoIntegrativo)} tipo="piu" />
                            )}
                            <Riga label="Netto mensile" valore={formatEur(mensile.netto)} tipo="totale" />
                        </tbody>
                    </table>
                    <p className="dettaglio-nota">
                        IRPEF effettiva del mese: {formatEur(mensile.irpefNetta)}.
                        {mese && ` Detrazioni calcolate sui ${d.busta.giorni} giorni di ${mese}, come fa il gestionale paghe.`}
                        {mese && d.busta.mese !== 12 && d.annuo.ulterioreDetrazione > 0 && ` L'ulteriore detrazione del cuneo (${formatEur(d.annuo.ulterioreDetrazione)} l'anno) non è nella busta del mese: il gestionale la riconosce tutta a dicembre, a conguaglio.`}
                        {aggiuntiveInBusta.length > 0 && ` A ${mese} in busta c'è anche la ${aggiuntiveInBusta.map(a => a.nome).join(" e la ")}: netto totale in busta ${formatEur(d.busta.nettoTotale)}.`}
                        La 13ª è tassata a parte: netto {formatEur(d.mensilitaAggiuntiva.netto)}.
                        {d.quattordicesima && ` La 14ª (${d.quattordicesima.quota === 0.5 ? "metà mensilità" : `${d.quattordicesima.quota} mensilità`}) anche: netto ${formatEur(d.quattordicesima.netto)}.`}
                        {d.sostitutiva && (d.sostitutiva.applicata
                            ? ` Aumento da rinnovo (${formatEur(mensile.imponibileSostitutiva)} al netto dei contributi) tassato al 5% invece che con l'IRPEF${d.sostitutiva.sceltaUtente ? "" : ": applicato in automatico perché il reddito stimato è sotto i 33.000 €"}.`
                            : " Tassa al 5% sugli aumenti del rinnovo non applicata.")}
                    </p>
                </div>

                <div className="dettaglio-box">
                    <h5 className="dettaglio-title">Costo azienda (annuo)</h5>
                    <table className="dettaglio-table">
                        <tbody>
                            <Riga label="Retribuzione lorda" valore={formatEur(costoAzienda.lordo)} />
                            <Riga label="INPS a carico azienda" valore={formatEur(costoAzienda.inps)} tipo="piu" />
                            <Riga label="INAIL" valore={formatEur(costoAzienda.inail)} tipo="piu" />
                            <Riga label="TFR" valore={formatEur(costoAzienda.tfr)} tipo="piu" />
                            <Riga label="Previdenza complementare" valore={formatEur(costoAzienda.previdenzaCompl)} tipo="piu" />
                            <Riga label="Assistenza sanitaria" valore={formatEur(costoAzienda.assistenzaSanitaria)} tipo="piu" />
                            {costoAzienda.indennitaTurno > 0 && (
                                <Riga label="Indennità turno" valore={formatEur(costoAzienda.indennitaTurno)} tipo="piu" />
                            )}
                            {costoAzienda.irap > 0 && (
                                <Riga label="IRAP" valore={formatEur(costoAzienda.irap)} tipo="piu" />
                            )}
                            <Riga label="Totale costo azienda" valore={formatEur(costoAzienda.totaleAnnuo)} tipo="totale" />
                        </tbody>
                    </table>
                </div>

                <div className="dettaglio-box">
                    <h5 className="dettaglio-title">Monte ore (annuo)</h5>
                    <table className="dettaglio-table">
                        <tbody>
                            <Riga label={`Ore teoriche (${formatOre(monteOre.oreGiornaliere)} h al giorno)`} valore={formatOre(monteOre.oreTeoriche)} />
                            <Riga label="Ferie" valore={formatOre(a.ferie)} tipo="meno" />
                            <Riga label="Ex festività" valore={formatOre(a.exFestivita)} tipo="meno" />
                            <Riga label="Festivi" valore={formatOre(a.festivi)} tipo="meno" />
                            <Riga label="Malattia, gravidanza, infortuni" valore={formatOre(a.malattia)} tipo="meno" />
                            {a.permessi > 0 && <Riga label="Permessi" valore={formatOre(a.permessi)} tipo="meno" />}
                            <Riga label="Assemblee sindacali" valore={formatOre(a.assemblee)} tipo="meno" />
                            <Riga label="Diritto allo studio" valore={formatOre(a.dirittoStudio)} tipo="meno" />
                            <Riga label="Formazione professionale" valore={formatOre(a.formazione)} tipo="meno" />
                            <Riga label="Formazione sicurezza" valore={formatOre(a.formazioneSicurezza)} tipo="meno" />
                            <Riga label="Ore lavorate" valore={formatOre(monteOre.oreLavorate)} tipo="totale" />
                        </tbody>
                    </table>
                    <p className="dettaglio-nota">
                        Assenteismo: {formatPerc(monteOre.percAssenteismo)} delle ore teoriche.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RisultatoRegole;
